import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import ShareMenuButton from '../../components/share/ShareMenuButton';
import { clubsService, marketplaceService, socialService } from '../../services';
import { useUser } from '../../UserContext';
import { getClubPublicPath, getClubStorefrontPath } from '../../utils/clubPublicPaths';
import './ClubProfile.css';

const formatDate = (dateValue) => {
    if (!dateValue) {
        return null;
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const ensureUrl = (url) => {
    if (!url) {
        return null;
    }

    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
};

const getSessionId = () => {
    const storageKey = 'sc_session_id';
    try {
        const existing = localStorage.getItem(storageKey);
        if (existing) {
            return existing;
        }

        const generated = `sc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(storageKey, generated);
        return generated;
    } catch (storageError) {
        return 'anonymous-session';
    }
};

const ClubProfile = () => {
    const { clubKey } = useParams();
    const navigate = useNavigate();
    const { initialized, isAuthenticated } = useUser();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [club, setClub] = useState(null);
    const [nearbyListings, setNearbyListings] = useState([]);
    const [venueListings, setVenueListings] = useState([]);
    const [isLoadingVenueListings, setIsLoadingVenueListings] = useState(false);
    const [isLoadingNearbyListings, setIsLoadingNearbyListings] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const trackedOfferImpressionIdsRef = useRef(new Set());
    const hasTrackedUpgradeViewRef = useRef(false);
    const hasTrackedProfileViewRef = useRef(false);

    const trackConversion = useCallback(async (eventType, extra = {}) => {
        if (!club?.clubId) {
            return;
        }

        try {
            await clubsService.trackClubConversionEvent(club.clubId, {
                eventType,
                source: 'ClubProfile',
                sessionId: getSessionId(),
                ...extra
            });
        } catch (trackError) {
            console.debug('Conversion tracking skipped:', trackError?.message || trackError);
        }
    }, [club]);

    useEffect(() => {
        const loadClub = async () => {
            setIsLoading(true);
            setError('');

            try {
                const data = await clubsService.getPublicClubProfile(clubKey);
                setClub(data);
                hasTrackedProfileViewRef.current = false;
                hasTrackedUpgradeViewRef.current = false;
                trackedOfferImpressionIdsRef.current = new Set();
            } catch (err) {
                setError(err.message || 'Unable to load venue profile right now.');
            } finally {
                setIsLoading(false);
            }
        };

        if (clubKey) {
            loadClub();
        }
    }, [clubKey]);

    useEffect(() => {
        if (!club?.publicKey || !clubKey || clubKey === club.publicKey) {
            return;
        }

        navigate(getClubPublicPath(club), { replace: true });
    }, [club, clubKey, navigate]);

    useEffect(() => {
        const fetchVenueListings = async () => {
        if (!club?.clubId) {
            setVenueListings([]);
            return;
        }

            setIsLoadingVenueListings(true);
            try {
                const listings = await marketplaceService.getClubListings(club.clubId, 6);
                setVenueListings(Array.isArray(listings) ? listings : []);
            } catch (listingError) {
                console.error('Failed to load venue marketplace listings:', listingError);
                setVenueListings([]);
            } finally {
                setIsLoadingVenueListings(false);
            }
        };

        fetchVenueListings();
    }, [club]);

    useEffect(() => {
        const fetchNearbyListings = async () => {
            if (!club?.address) {
                setNearbyListings([]);
                return;
            }

            const postcodeMatch = club.address.match(/[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}/i);
            const locationTerm = postcodeMatch?.[0] || club.address.split(',').slice(-1)[0].trim() || club.address;

            setIsLoadingNearbyListings(true);
            try {
                const params = new URLSearchParams();
                params.append('page', '1');
                params.append('pageSize', '4');
                params.append('sortBy', 'Newest');
                params.append('location', locationTerm);
                const data = await marketplaceService.getListings(params);
                setNearbyListings(data?.listings || []);
            } catch (listingError) {
                console.error('Failed to load nearby marketplace listings:', listingError);
                setNearbyListings([]);
            } finally {
                setIsLoadingNearbyListings(false);
            }
        };

        fetchNearbyListings();
    }, [club?.address]);

    const isPremiumProfile = useMemo(() => {
        const tier = (club?.profileTier || '').toLowerCase();
        return tier === 'premium';
    }, [club?.profileTier]);

    const profileLabel = isPremiumProfile ? 'Premium Venue Profile' : 'Free Venue Profile';
    const primaryPhoto = club?.photos?.[0]?.photoUrl;
    const websiteUrl = ensureUrl(club?.website);
    const heroPrimary = club?.primaryBrandColor || '#114b5f';
    const heroSecondary = club?.secondaryBrandColor || '#d97706';
    const storefrontUrl = getClubStorefrontPath(club);

    const activeOffers = useMemo(() => {
        if (!club?.offers?.length) {
            return [];
        }
        return [...club.offers].sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured));
    }, [club?.offers]);

    const upcomingEvents = club?.events || [];

    useEffect(() => {
        if (!club?.clubId || hasTrackedProfileViewRef.current) {
            return;
        }

        hasTrackedProfileViewRef.current = true;
        trackConversion('ProfileView');
    }, [club?.clubId, trackConversion]);

    useEffect(() => {
        if (!club?.clubId || !activeOffers.length) {
            return;
        }

        const nextOfferIds = activeOffers
            .map((offer) => offer.clubOfferId)
            .filter((offerId) => Number.isFinite(offerId) && !trackedOfferImpressionIdsRef.current.has(offerId));

        if (!nextOfferIds.length) {
            return;
        }

        nextOfferIds.forEach((offerId) => {
            trackedOfferImpressionIdsRef.current.add(offerId);
            trackConversion('PerksImpression', {
                clubOfferId: offerId,
                metadataJson: JSON.stringify({ location: 'PublicClubProfile' })
            });
        });
    }, [club?.clubId, activeOffers, trackConversion]);

    useEffect(() => {
        if (!club?.clubId || isPremiumProfile || hasTrackedUpgradeViewRef.current) {
            return;
        }

        hasTrackedUpgradeViewRef.current = true;
        trackConversion('UpgradePromptView');
    }, [club?.clubId, isPremiumProfile, trackConversion]);

    const getOfferStatus = (offer) => {
        if (!offer.endDate) {
            return 'Active';
        }

        const today = new Date();
        const endDate = new Date(offer.endDate);
        const msDiff = endDate.getTime() - today.getTime();
        const daysDiff = Math.ceil(msDiff / (1000 * 60 * 60 * 24));

        if (daysDiff <= 7) {
            return 'Expiring soon';
        }

        return 'Active';
    };

    const facilityItems = [
        { label: 'Cafe', value: club?.hasCafe },
        { label: 'Shop', value: club?.hasShop },
        { label: 'Toilets', value: club?.hasToilets },
        { label: 'Car Park', value: club?.hasCarpark }
    ];

    useEffect(() => {
        if (initialized && isAuthenticated && club?.clubId) {
            navigate(`/dashboard/clubs/${club.clubId}`, { replace: true });
        }
    }, [club, initialized, isAuthenticated, navigate]);

    const shareVenue = async (platform) => {
        try {
            const sharePlatform = platform === 'Copy' ? 'CopyLink' : platform;
            const data = await socialService.shareVenue(Number(club?.clubId), sharePlatform);
            if (!data?.shareUrl) return;

            if (platform === 'Copy') {
                await navigator.clipboard.writeText(data.shareUrl);
                return;
            }

            window.open(data.shareUrl, '_blank', 'noopener,noreferrer,width=700,height=550');
        } catch (error) {
            console.error('Error sharing venue:', error);
        }
    };

    return (
        <div className="club-profile-page">
            <Header openLoginModal={() => setIsLoginModalOpen(true)} alwaysSolid />

            <main className="club-profile-main">
                <div className="container">
                    <Link className="club-profile-back" to="/Clubs">
                        {'<-'} Back to venue search
                    </Link>
                    {!isLoading && !error && club && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                            <ShareMenuButton onShare={shareVenue} />
                        </div>
                    )}

                    {isLoading && (
                        <div className="club-profile-state">
                            <h2>Loading venue profile...</h2>
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="club-profile-state error">
                            <h2>We could not load this venue profile</h2>
                            <p>{error}</p>
                        </div>
                    )}

                    {!isLoading && !error && club && (
                        <>
                            <section
                                className={`club-profile-hero ${isPremiumProfile ? 'premium' : 'free'}`}
                                style={{ background: `linear-gradient(135deg, ${heroPrimary}, ${heroSecondary})` }}
                            >
                                <div className="club-profile-hero-content">
                                    <span className="club-profile-tier">{profileLabel}</span>
                                    {club.logoUrl ? (
                                        <img
                                            src={club.logoUrl}
                                            alt={`${club.name} logo`}
                                            style={{
                                                width: '82px',
                                                height: '82px',
                                                objectFit: 'cover',
                                                borderRadius: '20px',
                                                background: '#fff',
                                                padding: '8px',
                                                marginBottom: '16px'
                                            }}
                                        />
                                    ) : null}
                                    <h1>{club.name}</h1>
                                    <p>{club.introduction || club.description || 'Venue details coming soon.'}</p>
                                    <div className="club-profile-contact-row">
                                        {club.phoneNumber ? <span>{club.phoneNumber}</span> : null}
                                        {club.email ? <span>{club.email}</span> : null}
                                        {websiteUrl ? (
                                            <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                                                Visit website
                                            </a>
                                        ) : null}
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '18px' }}>
                                        {isPremiumProfile ? (
                                            <a
                                                href={storefrontUrl}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '12px 18px',
                                                    borderRadius: '999px',
                                                    background: '#fff',
                                                    color: heroPrimary,
                                                    fontWeight: 700,
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                Visit Shopfront
                                            </a>
                                        ) : null}
                                        <a
                                            href="#venue-perks"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '12px 18px',
                                                borderRadius: '999px',
                                                border: '1px solid rgba(255,255,255,0.45)',
                                                color: '#fff',
                                                fontWeight: 600,
                                                textDecoration: 'none'
                                            }}
                                        >
                                            Explore Venue Details
                                        </a>
                                    </div>
                                </div>
                                <div className="club-profile-hero-media">
                                    {primaryPhoto ? (
                                        <img src={primaryPhoto} alt={`${club.name} venue`} />
                                    ) : (
                                        <div className="club-profile-hero-placeholder">No hero image yet</div>
                                    )}
                                </div>
                            </section>

                            <section className="club-profile-stats">
                                <article><strong>{activeOffers.length}</strong><span>Live Perks</span></article>
                                <article><strong>{upcomingEvents.length}</strong><span>Upcoming Events</span></article>
                                <article><strong>{club.photos?.length || 0}</strong><span>Gallery Photos</span></article>
                                <article><strong>{venueListings.length}</strong><span>Venue Listings</span></article>
                            </section>

                            <div className="club-profile-content-layout">
                                <div className="club-profile-content-main">
                                    <section className="club-profile-card" id="venue-perks">
                                        <div className="club-profile-section-header">
                                            <h3>Connect Perks</h3>
                                            <span>{activeOffers.length} live</span>
                                        </div>
                                        <p className="club-muted">
                                            Show your Connect Pass QR at the venue desk to claim eligible perks.
                                        </p>
                                        {activeOffers.length > 0 ? (
                                            <div className="club-offers-grid">
                                                {activeOffers.map((offer) => (
                                                    <article className={`club-offer-card ${offer.isFeatured ? 'featured' : ''}`} key={offer.clubOfferId}>
                                                        <h4>{offer.title}</h4>
                                                        {offer.imageUrl ? (
                                                            <img
                                                                src={offer.imageUrl}
                                                                alt={offer.title}
                                                                style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '14px', marginBottom: '12px' }}
                                                            />
                                                        ) : null}
                                                        <p>{offer.shortDescription || offer.fullDescription || 'Offer details available on request.'}</p>
                                                        <div className="club-offer-meta">
                                                            <span>{getOfferStatus(offer)}</span>
                                                            {offer.offerType ? <span>{offer.offerType}</span> : null}
                                                            {offer.offerCode ? <span>Code {offer.offerCode}</span> : null}
                                                            {formatDate(offer.endDate) ? <span>Ends {formatDate(offer.endDate)}</span> : null}
                                                        </div>
                                                        {offer.ctaUrl ? (
                                                            <a
                                                                href={ensureUrl(offer.ctaUrl)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={() =>
                                                                    trackConversion('PerkClick', {
                                                                        clubOfferId: offer.clubOfferId,
                                                                        metadataJson: JSON.stringify({ location: 'PublicClubProfileCta' })
                                                                    })
                                                                }
                                                            >
                                                                {offer.ctaLabel || 'View perk'}
                                                            </a>
                                                        ) : null}
                                                    </article>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="club-muted">No active public perks published yet.</p>
                                        )}
                                    </section>

                                    <section className="club-profile-card">
                                        <div className="club-profile-section-header">
                                            <h3>Upcoming venue events</h3>
                                            <span>{upcomingEvents.length}</span>
                                        </div>
                                        {upcomingEvents.length > 0 ? (
                                            <div className="club-events-grid">
                                                {upcomingEvents.map((event) => (
                                                    <div key={event.eventId} className="club-event-card">
                                                        <strong>{event.name}</strong>
                                                        <span>{formatDate(event.eventDate) || 'Date to be confirmed'}</span>
                                                        {event.description ? <p>{event.description}</p> : null}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : isPremiumProfile ? (
                                            <p className="club-muted">No upcoming events published yet.</p>
                                        ) : (
                                            <p className="club-muted">Free profiles show limited event visibility.</p>
                                        )}
                                    </section>

                                    <section className="club-profile-card">
                                        <div className="club-profile-section-header">
                                            <h3>Marketplace listings from this venue</h3>
                                            {isPremiumProfile ? (
                                                <a href={storefrontUrl}>Visit shopfront</a>
                                            ) : null}
                                        </div>
                                        {isLoadingVenueListings ? (
                                            <p className="club-muted">Loading venue marketplace listings...</p>
                                        ) : venueListings.length === 0 ? (
                                            <>
                                                <p className="club-muted">This venue has not posted marketplace listings yet.</p>
                                                {isLoadingNearbyListings ? (
                                                    <p className="club-muted">Loading nearby marketplace listings...</p>
                                                ) : nearbyListings.length === 0 ? (
                                                    <p className="club-muted">No nearby listings are live yet. Check back soon for local listings.</p>
                                                ) : (
                                                    <div className="club-marketplace-grid">
                                                        {nearbyListings.map((listing) => (
                                                            <a
                                                                key={listing.listingId}
                                                                className="club-marketplace-card"
                                                                href={`/dashboard/marketplace/${listing.listingId}`}
                                                            >
                                                                <strong>{listing.title}</strong>
                                                                <span>{listing.category}</span>
                                                                <em>GBP {Number(listing.price || 0).toLocaleString('en-GB')}</em>
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="club-marketplace-grid">
                                                {(isPremiumProfile ? venueListings : venueListings.slice(0, 3)).map((listing) => (
                                                    <a
                                                        key={listing.listingId}
                                                        className="club-marketplace-card"
                                                        href={`/dashboard/marketplace/${listing.listingId}`}
                                                    >
                                                        <strong>{listing.title}</strong>
                                                        <span>{listing.category}</span>
                                                        <em>GBP {Number(listing.price || 0).toLocaleString('en-GB')}</em>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                        {!isPremiumProfile && venueListings.length > 3 ? (
                                            <p className="club-muted" style={{ marginTop: '12px' }}>
                                                Free venue pages show a lighter listing preview. Premium venues surface a dedicated shopfront button and the fuller storefront journey.
                                            </p>
                                        ) : null}
                                    </section>

                                    {isPremiumProfile ? (
                                        <section className="club-profile-card">
                                            <div className="club-profile-section-header">
                                                <h3>Premium highlights</h3>
                                                <span>{club.photos?.length || 0} photos</span>
                                            </div>
                                            <div className="club-gallery-grid">
                                                {(club.photos || []).map((photo) => (
                                                    <img key={photo.clubPhotoId} src={photo.photoUrl} alt={photo.description || `${club.name} gallery`} />
                                                ))}
                                            </div>
                                            {(club.photos || []).length === 0 ? (
                                                <p className="club-muted">This premium venue has not uploaded gallery images yet.</p>
                                            ) : null}
                                        </section>
                                    ) : null}
                                </div>

                                <aside className="club-profile-content-side">
                                    <article className="club-profile-card">
                                        <h3>About this venue</h3>
                                        <p>{club.description || 'This venue has not added a full description yet.'}</p>
                                        <p className="club-profile-address">{club.address}</p>
                                    </article>

                                    <article className="club-profile-card">
                                        <h3>Facilities</h3>
                                        <div className="club-facility-grid">
                                            {facilityItems.map((item) => (
                                                <span key={item.label} className={item.value ? 'on' : 'off'}>
                                                    {item.value ? 'Yes' : 'No'} {item.label}
                                                </span>
                                            ))}
                                        </div>
                                    </article>

                                    <section className="club-profile-card profile-compare">
                                        <h3>Own this venue?</h3>
                                        <p>Claim this listing and unlock admin tools for events, perks, members, and premium venue setup.</p>
                                        <a href="/register?intent=venue-claim">Start venue claim</a>
                                    </section>
                                </aside>
                            </div>
                        </>
                    )}
                </div>
            </main>

            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
            <Footer />
        </div>
    );
};

export default ClubProfile;
